'use client';

import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RefObject } from 'react';

interface ComboboxOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
  attribute?: any;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  className?: string;
  showNoneOption?: boolean;
  noneLabel?: string;
  containerRef?: RefObject<HTMLElement>;
}

export function Combobox({
  options,
  value = '',
  onValueChange,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  emptyText = 'No option found.',
  className = 'w-[200px]',
  showNoneOption = false,
  noneLabel = 'None',
  containerRef,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  // Unique placeholder for the "None" option to avoid conflicts
  const NONE_PLACEHOLDER = '__COMBOBOX_NONE_OPTION_7f8e9d0a__';

  // Group options by their group property
  const groupedOptions = React.useMemo(() => {
    const groups = new Map<string, ComboboxOption[]>();

    // Add "None" option if enabled
    if (showNoneOption) {
      groups.set('none', [{ value: NONE_PLACEHOLDER, label: noneLabel }]);
    }

    // Group regular options
    options.forEach((option) => {
      const groupName = option.group || 'default';
      if (!groups.has(groupName)) {
        groups.set(groupName, []);
      }
      groups.get(groupName)!.push(option);
    });

    return groups;
  }, [options, showNoneOption, noneLabel, NONE_PLACEHOLDER]);

  const selectedOption = React.useMemo(() => {
    if (value === '' && showNoneOption) {
      return { value: NONE_PLACEHOLDER, label: noneLabel };
    }
    return options.find((option) => option.value === value);
  }, [value, options, showNoneOption, noneLabel, NONE_PLACEHOLDER]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger
        className={cn(
          'border-input bg-background hover:bg-accent hover:text-accent-foreground inline-flex items-center justify-between gap-2 rounded-sm border px-3 py-2 text-sm shadow-xs',
          !selectedOption && !value && 'text-muted-foreground',
          className
        )}
        role="combobox"
        aria-expanded={open}
      >
        <div className="flex-1 min-w-0 overflow-hidden truncate text-left">
          {selectedOption ? selectedOption.label : value || placeholder}
        </div>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent
        className={cn('p-0', className)}
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {Array.from(groupedOptions.entries()).map(
              ([groupName, groupOptions]) => (
                <CommandGroup
                  key={groupName}
                  heading={
                    groupName !== 'none' && groupName !== 'default'
                      ? groupName
                      : undefined
                  }
                >
                  {groupOptions.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.label}
                      disabled={option.disabled}
                      onSelect={(currentLabel) => {
                        if (option.disabled) return;

                        // Find the option by label to get its actual value
                        const selectedOption = groupOptions.find(
                          (opt) => opt.label === currentLabel
                        );
                        if (!selectedOption) return;

                        // Handle the special none placeholder case
                        const newValue =
                          selectedOption.value === NONE_PLACEHOLDER
                            ? ''
                            : selectedOption.value;
                        const finalValue = newValue === value ? '' : newValue;
                        onValueChange(finalValue);
                        setOpen(false);
                      }}
                      className={cn(
                        option.disabled && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          (value === '' && option.value === NONE_PLACEHOLDER) ||
                            value === option.value
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                      />
                      {option.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
Combobox.displayName = 'Combobox';
